import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faPaperPlane,
  faImage,
  faTimes,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { getAllCropsForBot, getAllProductsForBot, getProblemDetailForBot } from './getChatBotData';
import botAvatar from '../../assets/images/news-bot.png';
import S from './styles';

// ─── Static reference data (from Crop.js) ────────────────────────────────────
const PROBLEM_DETAILS = {
  "Early Blight": {
    description: "Brown spots with concentric rings on lower leaves, spreading upward and causing defoliation.",
    causes: ["Fungal infection (Alternaria solani)", "High humidity & wet conditions", "Nutrient-stressed plants"],
    symptoms: ["Dark brown circular spots with yellow halos", "Concentric ring pattern inside spots", "Premature leaf drop"],
    solution: { type: "Foliar Spray", product: "Mancozeb 75 WP" },
    dosage: ["2.5 g per liter of water", "Spray every 7–10 days", "3–4 sprays per season"],
    application: ["Spray on both leaf surfaces", "Apply in early morning or evening", "Avoid spraying during rain"],
  },
  "Late Blight": {
    description: "Water-soaked lesions on leaves and stems that turn brown with white fungal growth on undersides.",
    causes: ["Phytophthora infestans (fungal-like pathogen)", "Cool, wet & cloudy weather", "Poor air circulation in canopy"],
    symptoms: ["Water-soaked spots turning brown rapidly", "White mold on leaf undersides", "Dark brown lesions on stems"],
    solution: { type: "Preventive Spray", product: "Metalaxyl + Mancozeb 72 WP" },
    dosage: ["3 g per liter of water", "Spray every 7 days preventively"],
    application: ["Apply before rain forecast", "Ensure thorough plant coverage", "Alternate with Cymoxanil"],
  },
  "Powdery Mildew": {
    description: "White powdery coating on leaves, stems and fruits reducing photosynthesis and weakening the plant.",
    causes: ["Fungal infection (Erysiphe/Podosphaera spp.)", "Dry weather with high relative humidity", "Dense planting & poor ventilation"],
    symptoms: ["White powder on leaf surface", "Yellowing and curling of leaves", "Stunted plant growth"],
    solution: { type: "Foliar Spray", product: "Sulphur 80 WP or Hexaconazole 5 EC" },
    dosage: ["Sulphur: 3 g/L of water", "Hexaconazole: 1 ml/L of water", "2–3 sprays at 10-day intervals"],
    application: ["Spray in early morning", "Cover leaf undersides thoroughly", "Do not apply sulphur above 35°C"],
  },
  "Leaf Curl Virus": {
    description: "Viral disease causing upward curling of leaves with yellow margins and severe yield loss.",
    causes: ["Begomovirus transmitted by whitefly", "High whitefly population", "Nearby infected weed hosts"],
    symptoms: ["Upward leaf curling with yellow margins", "Thick, leathery, brittle leaves", "Stunted growth & poor fruit set"],
    solution: { type: "Vector Control", product: "Imidacloprid 17.8 SL + Yellow Sticky Traps" },
    dosage: ["0.5 ml Imidacloprid per liter of water", "Spray every 15 days"],
    application: ["Target leaf undersides where whitefly feed", "Remove and destroy infected plants", "Plant border crops to trap pests"],
  },
  "Bacterial Wilt": {
    description: "Sudden wilting of entire plants due to bacterial blockage of water-conducting vascular tissue.",
    causes: ["Ralstonia solanacearum bacteria", "Contaminated soil or irrigation water", "High soil moisture & warm temperatures"],
    symptoms: ["Sudden wilting without prior yellowing", "Brown ring in stem cross-section", "Bacterial ooze test: milky stream in water"],
    solution: { type: "Soil Drench (Preventive)", product: "Copper Oxychloride 50 WP" },
    dosage: ["3 g per liter of water", "Drench at base of plants"],
    application: ["No cure — uproot infected plants immediately", "Drench nearby plants as protection", "Avoid waterlogging; improve drainage"],
  },
  "Blossom End Rot": {
    description: "Dark brown or black necrotic spot at the blossom end of fruit, progressing inward.",
    causes: ["Calcium deficiency in developing fruit", "Irregular irrigation causing Ca uptake failure", "Excess nitrogen fertilizer"],
    symptoms: ["Dark spot at fruit bottom", "Firm, leathery patch on fruit tip", "Spot enlarges as fruit grows"],
    solution: { type: "Foliar Spray", product: "Calcium Nitrate" },
    dosage: ["2–3 gram per liter of water", "Spray weekly during fruit development"],
    application: ["Spray on leaves and developing fruits", "Maintain consistent drip irrigation", "Reduce ammoniacal nitrogen use"],
  },
  Nematodes: {
    description: "Microscopic roundworms in soil causing root galls, stunted growth and nutrient deficiency symptoms.",
    causes: ["Root-knot nematode (Meloidogyne spp.)", "Infested soil or planting material", "Sandy, warm soil conditions"],
    symptoms: ["Swollen root galls (knots)", "Stunted and yellowing plants", "Wilting despite adequate irrigation"],
    solution: { type: "Soil Treatment", product: "Carbofuran 3G or Neem Cake" },
    dosage: ["Carbofuran: 1 kg per 500 sq m", "Neem cake: 250 kg/acre"],
    application: ["Apply before planting & mix into soil", "Follow with irrigation", "Use resistant varieties where available"],
  },
  Yellowing: {
    description: "Interveinal or whole-leaf yellowing indicating nutrient deficiency or pathogen infection.",
    causes: ["Iron or Nitrogen deficiency", "Magnesium deficiency in sandy soils", "Root damage reducing nutrient uptake"],
    symptoms: ["Pale yellow leaves (younger or older depending on nutrient)", "Green veins with yellow interveinal tissue", "Reduced growth"],
    solution: { type: "Foliar Spray + Soil Application", product: "Ferrous Sulphate / Urea" },
    dosage: ["FeSO₄: 5 g/L with citric acid 1 g/L", "Urea: 1% foliar spray"],
    application: ["Spray 2–3 times at weekly intervals", "Test soil pH — correct if >7.5 for Fe deficiency"],
  },
};

const getGenericDetail = (problemText, catName) => ({
  description: `${problemText} is a ${catName || "common"} problem affecting crop health and yield. Early identification and timely management are critical.`,
  causes: ["Pathogen or environmental stress", "Favorable weather conditions for disease", "Susceptible crop variety"],
  symptoms: ["Visible damage on leaves, stem or fruit", "Reduced growth and vigor", "Potential yield loss if untreated"],
  solution: { type: "Consult agronomist", product: "Specific product recommendation required" },
  dosage: ["Follow recommended product label", "Apply at prescribed intervals"],
  application: ["Apply in early morning or evening", "Ensure complete plant coverage"],
});

const getProblemDetail = (problemText, catName) =>
  PROBLEM_DETAILS[problemText] || getGenericDetail(problemText, catName);

// ─── Query Engine ─────────────────────────────────────────────────────────────
const tokenize = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[_.-]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length >= 2);

const scoreText = (target, tokens) => {
  const t = (target || "").toLowerCase();
  return tokens.reduce((acc, tok) => (t.includes(tok) ? acc + 1 : acc), 0);
};

const processQuery = (inputText, imageFileName, cropsData, productsData) => {
  const fileTokens = imageFileName ? tokenize(imageFileName.replace(/\.[^.]+$/, "")) : [];
  const inputTokens = tokenize(inputText);
  const tokens = [...new Set([...inputTokens, ...fileTokens])];

  if (!tokens.length) return { found: false };

  const results = [];

  // Score products
  productsData.forEach((product) => {
    let score = 0;
    const nameScore = scoreText(product.product, tokens) * 3;
    const subtitleScore = scoreText(product.subtitle, tokens) * 2;
    const descScore = scoreText(product.shortDescription, tokens) * 2;
    const dosageScore = scoreText(
      Array.isArray(product.advisoryDosage)
        ? product.advisoryDosage.map((d) => `${d.crop} ${d.dosage}`).join(" ")
        : String(product.advisoryDosage || ""),
      tokens
    );
    score = nameScore + subtitleScore + descScore + dosageScore;
    if (score > 0) {
      results.push({ type: 'product', score, data: product });
    }
  });

  // Score crop problems
  cropsData.forEach((crop) => {
    const cropScore = scoreText(crop.crop, tokens) * 3;
    (crop.categories || []).forEach((cat) => {
      const catScore = cropScore + scoreText(cat.category, tokens) * 2;
      (cat.problems || []).forEach((problem) => {
        const probScore = catScore + scoreText(problem.problemText, tokens) * 4;
        if (probScore > 0) {
          results.push({
            type: 'crop_problem',
            score: probScore,
            data: {
              cropName: crop.crop,
              category: cat.category,
              problem,
              detail: getProblemDetail(problem.problemText, cat.category),
            },
          });
        }
      });
    });
  });

  // Sort, deduplicate, take top 3.
  // Dedup by content (crop + category + problem text), not just _id — the
  // crop data can contain multiple problem records with identical text (e.g.
  // from past duplicate-entry data issues), and those should still collapse
  // into a single result instead of repeating the same card 2-3 times.
  results.sort((a, b) => b.score - a.score);
  const seen = new Set();
  const deduped = [];
  for (const r of results) {
    const key =
      r.type === 'product'
        ? `p_${r.data._id}`
        : `c_${(r.data.cropName || '').trim().toLowerCase()}_${(r.data.category || '').trim().toLowerCase()}_${(r.data.problem.problemText || '').trim().toLowerCase()}`;
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(r);
    }
    if (deduped.length >= 3) break;
  }

  return deduped.length ? { found: true, results: deduped } : { found: false };
};

// ─── Result Card ──────────────────────────────────────────────────────────────
const ResultCard = ({ card }) => {
  if (card.type === 'product') {
    const p = card.data;
    return (
      <div style={S.resultCard}>
        <div style={S.cardBadgeProduct}>Product</div>
        <div style={S.cardTitle}>{p.product}</div>
        {p.subtitle && <div style={S.cardSubtitle}>{p.subtitle}</div>}
        {p.shortDescription && <div style={S.cardRow}>{p.shortDescription}</div>}

        {/* Advisory dosage table */}
        {Array.isArray(p.advisoryDosage) && p.advisoryDosage.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ ...S.cardRow, fontWeight: 700, marginBottom: 4 }}>Advisory Dosage</div>
            <table style={tbl.table}>
              <thead>
                <tr>
                  <th style={tbl.th}>Crop</th>
                  <th style={tbl.th}>Dose</th>
                  <th style={tbl.th}>Frequency</th>
                </tr>
              </thead>
              <tbody>
                {p.advisoryDosage.map((d, i) => (
                  <tr key={i} style={i % 2 === 0 ? tbl.trEven : {}}>
                    <td style={tbl.td}>{d.crop || '—'}</td>
                    <td style={tbl.td}>{d.dosage || '—'}</td>
                    <td style={tbl.td}>{d.frequency || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {Array.isArray(p.packing) && p.packing.length > 0 && (
          <div style={{ ...S.cardRow, marginTop: 6 }}><strong>Packing:</strong> {p.packing.join(', ')}</div>
        )}
      </div>
    );
  }

  if (card.type === 'crop_problem') {
    const { cropName, category, problem, detail } = card.data;
    const hasBackendData = detail && (
      detail.causes?.length || detail.symptoms?.length ||
      detail.solutions?.some(s => s.products?.some(p => p.productName))
    );

    return (
      <div style={S.resultCardCrop}>
        <div style={S.cardBadgeCrop}>Reference Material</div>

        {/* 1. Problem Name */}
        <div style={rc.sectionLabel}>1. Problem Name</div>
        <div style={S.cardTitle}>{detail?.problemName || problem.problemText}</div>
        <div style={S.cardSubtitle}>{cropName} · {category}</div>

        {!hasBackendData && (
          <div style={{ ...S.cardRow, color: '#999', fontStyle: 'italic' }}>
            Reference material not yet configured for this problem.
          </div>
        )}

        {/* 2. Causes */}
        {detail?.causes?.length > 0 && (
          <>
            <div style={rc.sectionLabel}>2. Causes</div>
            <ul style={rc.list}>
              {detail.causes.map((c, i) => <li key={i} style={rc.listItem}>{c}</li>)}
            </ul>
          </>
        )}

        {/* 3. Symptoms */}
        {detail?.symptoms?.length > 0 && (
          <>
            <div style={rc.sectionLabel}>3. Symptoms</div>
            <ul style={rc.list}>
              {detail.symptoms.map((s, i) => <li key={i} style={rc.listItem}>{s}</li>)}
            </ul>
          </>
        )}

        {/* 4. Solutions */}
        {detail?.solutions?.length > 0 && detail.solutions.some(s => s.name || s.products?.some(p => p.productName)) && (
          <>
            <div style={rc.sectionLabel}>4. Solutions</div>
            {detail.solutions.map((sol, si) => (
              <div key={si} style={rc.solutionBlock}>
                {sol.name && <div style={rc.solutionName}>{sol.name}</div>}
                {sol.products?.some(p => p.productName) && (
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        <th style={tbl.th}>#</th>
                        <th style={tbl.th}>Product Name</th>
                        <th style={tbl.th}>Dose</th>
                        <th style={tbl.th}>Application Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sol.products.map((prod, pi) => (
                        <tr key={pi} style={pi % 2 === 0 ? tbl.trEven : {}}>
                          <td style={tbl.td}>{pi + 1}</td>
                          <td style={tbl.td}>{prod.productName || '—'}</td>
                          <td style={tbl.td}>{prod.dose || '—'}</td>
                          <td style={tbl.td}>{prod.applicationMethod || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </>
        )}

        {/* 5. Photo */}
        {detail?.images?.length > 0 && (
          <>
            <div style={rc.sectionLabel}>5. Photo</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              {detail.images.slice(0, 3).map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  style={rc.photo}
                  onError={(e) => (e.target.style.display = 'none')}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
};

// ─── Table & card sub-styles ──────────────────────────────────────────────────
const tbl = {
  table:  { width: '100%', borderCollapse: 'collapse', marginTop: 4, fontSize: '0.75rem' },
  th:     { backgroundColor: '#2A6B2E', color: '#fff', padding: '4px 7px', textAlign: 'left', fontWeight: 600, fontSize: '0.72rem' },
  td:     { padding: '4px 7px', borderBottom: '1px solid #e0e0e0', verticalAlign: 'top', color: '#333' },
  trEven: { backgroundColor: '#f4fbf4' },
};
const rc = {
  sectionLabel: { fontSize: '0.67rem', fontWeight: 800, color: '#2A6B2E', letterSpacing: '0.07em', textTransform: 'uppercase', marginTop: 10, marginBottom: 3 },
  list:         { margin: '0 0 0 14px', padding: 0, fontSize: '0.79rem', color: '#333' },
  listItem:     { marginBottom: 2, lineHeight: 1.4 },
  solutionBlock:{ backgroundColor: '#fff', borderRadius: 7, padding: '7px 8px', marginBottom: 6, border: '1px solid #d0e8d0' },
  solutionName: { fontWeight: 700, fontSize: '0.8rem', color: '#2A6B2E', marginBottom: 5 },
  photo:        { width: 80, height: 70, objectFit: 'cover', borderRadius: 7, border: '1px solid #ddd' },
};

// ─── Message Bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ msg }) => {
  const timeStr = moment(msg.timestamp).fromNow();
  if (msg.type === 'user') {
    return (
      <div style={{ alignSelf: 'flex-end', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '80%' }}>
        <div style={S.userBubble}>
          {msg.imageUrl && (
            <img src={msg.imageUrl} alt="uploaded" style={S.uploadedImageThumb} />
          )}
          {msg.text && <span>{msg.text}</span>}
        </div>
        <div style={{ ...S.msgTime, textAlign: 'right' }}>{timeStr}</div>
      </div>
    );
  }

  return (
    <div style={S.botWrapper}>
      <div style={S.botBubble}>{msg.text}</div>
      {msg.cards && msg.cards.map((card, i) => <ResultCard key={i} card={card} />)}
      <div style={S.msgTime}>{timeStr}</div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ChatBot = () => {
  const [isOpen,        setIsOpen]        = useState(false);
  const [cropsData,     setCropsData]     = useState([]);
  const [productsData,  setProductsData]  = useState([]);
  const [dataLoaded,    setDataLoaded]    = useState(false);
  const [dataLoading,   setDataLoading]   = useState(false);
  const [messages,      setMessages]      = useState([]);
  const [inputText,     setInputText]     = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isTyping,      setIsTyping]      = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef   = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const loadData = useCallback(async () => {
    setDataLoading(true);
    const [cropsRes, productsRes] = await Promise.all([
      getAllCropsForBot(),
      getAllProductsForBot(),
    ]);

    // Log raw responses so you can inspect them in browser DevTools console
    console.group('[ChatBot] Raw API responses');
    console.log('Crops raw response:', cropsRes);
    console.log('Products raw response:', productsRes);
    console.groupEnd();

    const crops    = cropsRes?.data?.data    ?? cropsRes?.data    ?? [];
    const products = productsRes?.data?.data ?? productsRes?.data ?? [];
    const cropsArr    = Array.isArray(crops)    ? crops    : [];
    const productsArr = Array.isArray(products) ? products : [];

    console.group('[ChatBot] Parsed data');
    console.log(`Crops loaded: ${cropsArr.length}`, cropsArr);
    console.log(`Products loaded: ${productsArr.length}`, productsArr);
    console.groupEnd();

    setCropsData(cropsArr);
    setProductsData(productsArr);
    setDataLoaded(true);
    setDataLoading(false);
  }, []);

  const handleToggle = useCallback(() => {
    if (!isOpen && !dataLoaded && !dataLoading) {
      loadData();
      setMessages([
        {
          id: 1,
          type: 'bot',
          text: "Hello! I'm Manshya AI 🌱 Ask me about crops, diseases, or products. You can also upload a crop image for help.",
          cards: [],
          timestamp: new Date(),
        },
      ]);
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, dataLoaded, dataLoading, loadData]);

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text && !uploadedImage) return;

    const userMsg = {
      id: Date.now(),
      type: 'user',
      text,
      imageUrl: uploadedImage?.previewUrl || null,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputText('');
    const imgForQuery = uploadedImage;
    setUploadedImage(null);

    setIsTyping(true);

    if (dataLoading) {
      setIsTyping(false);
      setMessages((prev) => [...prev, {
        id: Date.now() + 1, type: 'bot', timestamp: new Date(), cards: [],
        text: 'Still loading knowledge base, please try again in a moment.',
      }]);
      return;
    }

    if (!text && imgForQuery) {
      setIsTyping(false);
      setMessages((prev) => [...prev, {
        id: Date.now() + 1, type: 'bot', timestamp: new Date(), cards: [],
        text: 'I can see your image. Please also type the crop name or problem you are facing so I can find accurate information for you.',
      }]);
      return;
    }

    const result = processQuery(text, imgForQuery?.name || '', cropsData, productsData);

    if (!result.found) {
      setIsTyping(false);
      setMessages((prev) => [...prev, {
        id: Date.now() + 1, type: 'bot', timestamp: new Date(), cards: [],
        text: 'Sorry, that information is not available in our database. Please contact our agronomist team for further assistance.',
      }]);
      return;
    }

    // For crop_problem results fetch real reference material from backend
    const enriched = await Promise.all(
      result.results.map(async (r) => {
        if (r.type !== 'crop_problem') return r;
        const res = await getProblemDetailForBot(r.data.problem._id);
        const backendDetail = res?.data;
        if (backendDetail && typeof backendDetail === 'object') {
          // Normalise: backend may return old format (solutionType/solutionProduct)
          const solutions = backendDetail.solutions || (
            backendDetail.solutionType
              ? [{ name: backendDetail.solutionType, products: [{ productName: backendDetail.solutionProduct || '', dose: (backendDetail.dosage || [])[0] || '', applicationMethod: (backendDetail.application || [])[0] || '' }] }]
              : r.data.detail?.solutions || []
          );
          return {
            ...r,
            data: {
              ...r.data,
              detail: {
                problemName: backendDetail.problemName || r.data.problem.problemText,
                causes:   backendDetail.causes   || [],
                symptoms: backendDetail.symptoms || [],
                solutions,
                images:   backendDetail.images   || [],
              },
            },
          };
        }
        return r;
      })
    );

    setIsTyping(false);
    setMessages((prev) => [...prev, {
      id: Date.now() + 1,
      type: 'bot',
      text: `Found ${enriched.length} result${enriched.length > 1 ? 's' : ''} for your query:`,
      cards: enriched,
      timestamp: new Date(),
    }]);
  }, [inputText, uploadedImage, cropsData, productsData, dataLoading]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImage({ file, previewUrl: reader.result, name: file.name });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <>
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        .chatbot-panel { animation: chatSlideUp 0.22s ease-out; }
        @keyframes typingBounce {
          0%, 80%, 100% { transform: scale(0.65); opacity: 0.35; }
          40%           { transform: scale(1.15); opacity: 1;    }
        }
        .cb-dot:nth-child(1) { animation: typingBounce 1.2s 0.0s infinite; }
        .cb-dot:nth-child(2) { animation: typingBounce 1.2s 0.2s infinite; }
        .cb-dot:nth-child(3) { animation: typingBounce 1.2s 0.4s infinite; }
        .chatbot-fab:hover { transform: scale(1.1) !important; box-shadow: 0 6px 28px rgba(61,138,66,0.65) !important; }
        .chatbot-close:hover { background: rgba(255,255,255,0.28) !important; }
        .chatbot-send:hover { opacity: 0.85; }
        .chatbot-icon-btn:hover { color: #29ABE2 !important; }
        .chatbot-input:focus { border-color: #3D8A42 !important; box-shadow: 0 0 0 3px rgba(61,138,66,0.12); }
      `}</style>

      {/* FAB */}
      <button
        className="chatbot-fab"
        style={S.fab}
        onClick={handleToggle}
        title="Ask Manshya AI"
        aria-label="Open AI assistant"
      >
        <FontAwesomeIcon icon={faRobot} />
      </button>

      {/* "Ask AI" pill label — only when panel is closed */}
      {!isOpen && (
        <div style={S.fabLabel}>Ask AI</div>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="chatbot-panel" style={S.panel}>

          {/* Header */}
          <div style={S.header}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <img src={botAvatar} alt="Manshya AI" style={S.headerAvatar} />
              <div style={{ ...S.headerDot, position: "absolute", bottom: 1, right: 1 }} />
            </div>
            <div style={S.headerInfo}>
              <div style={S.headerTitle}>Manshya AI</div>
              <div style={S.headerSubtitle}>Crop &amp; Product Assistant · Online</div>
            </div>
            <button className="chatbot-close" style={S.closeBtn} onClick={handleToggle} aria-label="Close chat">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Data loading notice */}
          {dataLoading && (
            <div style={S.dataLoadingMsg}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 6 }} />
              Loading knowledge base…
            </div>
          )}

          {/* Messages */}
          <div style={S.messageArea}>
            {messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} />
            ))}

            {isTyping && (
              <div style={S.typingIndicator}>
                <div className="cb-dot" style={S.typingDotGreen} />
                <div className="cb-dot" style={S.typingDotYellow} />
                <div className="cb-dot" style={S.typingDotBlue} />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Staged image preview */}
          {uploadedImage && (
            <div style={S.previewStrip}>
              <img src={uploadedImage.previewUrl} alt="" style={S.previewStripThumb} />
              <span style={S.previewStripName}>{uploadedImage.name}</span>
              <button style={S.previewRemove} onClick={() => setUploadedImage(null)} aria-label="Remove image">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}

          {/* Input row */}
          <div style={S.inputRow}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
            <button
              className="chatbot-icon-btn"
              style={S.iconBtn}
              onClick={() => fileInputRef.current?.click()}
              title="Upload crop image"
              aria-label="Upload image"
            >
              <FontAwesomeIcon icon={faImage} />
            </button>
            <input
              className="chatbot-input"
              style={S.textInput}
              placeholder="Ask about a crop or product…"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="chatbot-send"
              style={S.sendBtn}
              onClick={handleSend}
              title="Send"
              aria-label="Send message"
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>

        </div>
      )}
    </>
  );
};

export default ChatBot;
